import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Box,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
} from '@mui/material';

const drawerWidth = 240;

export default function EditAssignment() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignmentData, setAssignmentData] = useState(null);
  const [classes, setClasses] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch assignment data
  useEffect(() => {
    const fetchAssignmentData = async () => {
      if (!assignmentId || isNaN(parseInt(assignmentId))) {
        setError('No valid assignment ID provided');
        return;
      }

      try {
        const assignmentIdInt = parseInt(assignmentId);

        const response = await fetch(
          'https://wsms.aidigitalsec.com/api/AssignmentCreate/GetAssignmentsById',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: sessionStorage.getItem('token') || '',
            },
            body: JSON.stringify({ assignmentId: assignmentIdInt }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText}`);
        }

        const data = await response.json();

        let assignment = null;
        if (Array.isArray(data.data) && data.data.length > 0) {
          assignment = data.data[0];
        } else if (data.data) {
          assignment = data.data;
        } else if (Array.isArray(data) && data.length > 0) {
          assignment = data[0];
        } else if (data.result) {
          assignment = data.result;
        } else {
          assignment = data;
        }

        if (!assignment || !assignment.assignmentId) {
          throw new Error('Assignment data is empty or missing assignmentId');
        }

        setAssignmentData({
          assignmentId: assignment.assignmentId ?? '',
          classId: assignment.classId != null ? Number(assignment.classId) : '',
          sectionId: assignment.sectionId != null ? Number(assignment.sectionId) : '',
          subjectId: assignment.subjectId != null ? Number(assignment.subjectId) : '',
          teacherId: assignment.teacherId != null ? Number(assignment.teacherId) : '',
          assignmentDescription: assignment.assignmentDescription ?? '',
          assignmentdate: assignment.assignmentDate ?? '',
          submissiondate: assignment.submissionDate ?? '',
          assignmentFilePath: assignment.assignmentFile ?? '',
          subjectName: assignment.subjectName ?? 'Unknown',
        });
      } catch (error) {
        console.error('Error fetching assignment data:', error);
        setError(`Failed to load assignment data: ${error.message}`);
      }
    };

    fetchAssignmentData();
  }, [assignmentId]);

  // Fetch classes
  useEffect(() => {
    const fetchClasses = async () => {
      let teacherId = sessionStorage.getItem('teacherId') || sessionStorage.getItem('employeeId');
      if (!teacherId || isNaN(parseInt(teacherId))) {
        setError('Teacher ID is not available or invalid.');
        setClasses([]);
        return;
      }

      try {
        const teacherIdInt = parseInt(teacherId);
        const response = await fetch('https://wsms.aidigitalsec.com/api/AssignmentCreate/GetClass', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: sessionStorage.getItem('token') || '',
          },
          body: JSON.stringify({ teacherId: teacherIdInt }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText}`);
        }

        const data = await response.json();
        if (data.data === null && data.msg === 'Record Not Found') {
          throw new Error('No classes found for this teacher');
        }
        setClasses(Array.isArray(data) ? data : data.data || []);
      } catch (error) {
        console.error('Error fetching classes:', error);
        setClasses([]);
      }
    };

    fetchClasses();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAssignmentData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!assignmentData) {
      setError('No assignment data to save');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('ClassId', assignmentData.classId || '0');
      formData.append('SectionId', assignmentData.sectionId || '0');
      formData.append('SubjectId', assignmentData.subjectId || '0');
      formData.append('TeacherId', assignmentData.teacherId || '0');
      formData.append('AssignmentDescription', assignmentData.assignmentDescription || '');
      formData.append('AssignmentDate', assignmentData.assignmentdate || '');
      formData.append('AssignmentSubmitDate', assignmentData.submissiondate || '');
      formData.append('AssignmentFilePath', assignmentData.assignmentFilePath || '');

      const response = await fetch(`https://wsms.aidigitalsec.com/api/AssignmentCreate/Id?Id=${assignmentData.assignmentId}`, {
        method: 'PUT',
        headers: {
          Authorization: sessionStorage.getItem('token') || '',
        },
        body: formData,
      });

      if (response.ok) {
        setSnackbar({ open: true, message: 'Assignment updated successfully!', severity: 'success' });
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        const errorText = await response.text();
        setSnackbar({ open: true, message: `Failed to save: ${errorText}`, severity: 'error' });
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
      setSnackbar({ open: true, message: `Failed to save: ${error.message}`, severity: 'error' });
    }
  };

  if (error && !assignmentData) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => navigate(-1)} sx={{ mt: 3 }}>
          Back to Assignments
        </Button>
      </Box>
    );
  }

  if (!assignmentData) {
    return <Typography sx={{ textAlign: 'center', mt: 8 }}>Loading...</Typography>;
  }

  return (
    <Box
      component="main"
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        mt: { xs: 0, sm: '64px' },
        p: { xs: 2, md: 4 },
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
      }}
    >
      <Card sx={{ maxWidth: 900, mx: 'auto', boxShadow: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              fontWeight: 'bold',
              color: '#1976d2',
              textAlign: 'center',
            }}
          >
            Edit Assignment
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Assignment ID"
                value={assignmentData.assignmentId || ''}
                disabled
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Assignment Date"
                name="assignmentdate"
                value={assignmentData.assignmentdate || ''}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Submission Date"
                name="submissiondate"
                value={assignmentData.submissiondate || ''}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Class</InputLabel>
                <Select
                  name="classId"
                  value={assignmentData.classId === '' ? '' : Number(assignmentData.classId)}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : Number(e.target.value);
                    setAssignmentData((prev) => ({ ...prev, classId: v }));
                  }}
                  label="Class"
                >
                  <MenuItem value="">Select Class</MenuItem>
                  {classes.map((classOption) => (
                    <MenuItem key={classOption.classId} value={Number(classOption.classId)}>
                      {classOption.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Subject ID"
                name="subjectId"
                value={assignmentData.subjectId || ''}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Teacher ID"
                name="teacherId"
                value={assignmentData.teacherId ?? ''}
                disabled
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Section ID"
                name="sectionId"
                value={assignmentData.sectionId ?? ''}
                disabled
                fullWidth
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                name="assignmentDescription"
                value={assignmentData.assignmentDescription || ''}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                multiline
                rows={4}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="File Path"
                name="assignmentFilePath"
                value={assignmentData.assignmentFilePath || ''}
                onChange={handleChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center', gap: 3 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleSave}
              sx={{ minWidth: 220 }}
            >
              Save Changes
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="large"
              onClick={() => navigate(-1)}
              sx={{ minWidth: 220 }}
            >
              Cancel
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}